import { useEffect, useState } from 'react'
import { Plus, Trash2, Pencil } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  IconButton,
  Input,
  Modal,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTable,
} from '@/components/ui'
import type { EmploymentType, FoerderungMode, PersonnelEntry } from './types.ts'

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

const DEFAULT_RATE: Record<EmploymentType, number> = { vollzeit: 22, teilzeit: 22, minijob: 30 }
const TYPE_LABEL: Record<EmploymentType, string> = { vollzeit: 'Vollzeit', teilzeit: 'Teilzeit', minijob: 'Minijob' }

/** Mittlerer Monats-Netto-Aufwand (inkl. Nebenkosten − Foerderung) fuer die Anzeige. */
function monthlyNetto(e: PersonnelEntry): number {
  const rate = (e.nebenkostenRatePct ?? DEFAULT_RATE[e.employmentType]) / 100
  const aufwand = e.grossMonthly * (1 + rate)
  let foerderung = 0
  if (e.foerderungMode !== 'none' && e.foerderungValue != null) {
    foerderung = e.foerderungMode === 'percent' ? aufwand * (e.foerderungValue / 100) : e.foerderungValue
  }
  return Math.round((aufwand - foerderung) * 100) / 100
}

function fmtPeriod(e: PersonnelEntry): string {
  const start = `${MONTH_LABELS[e.startMonth - 1]} ${e.startYear}`
  const end = e.endYear !== null && e.endMonth !== null
    ? `${MONTH_LABELS[e.endMonth - 1]} ${e.endYear}`
    : '∞'
  return `${start} – ${end}`
}

export function PersonnelTab({ planId, year }: { planId: number; year: number }) {
  const [entries, setEntries] = useState<PersonnelEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editEntry, setEditEntry] = useState<PersonnelEntry | null>(null)

  async function load() {
    setLoading(true)
    try {
      const res = await apiClient.get<PersonnelEntry[]>(`/liquidity-planning/plans/${planId}/personnel`)
      setEntries(Array.isArray(res.data) ? res.data : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId])

  async function handleDelete(id: number) {
    if (!confirm('Mitarbeiter wirklich löschen?')) return
    await apiClient.delete(`/liquidity-planning/plans/${planId}/personnel/${id}`)
    void load()
  }

  const totalNetto = entries.reduce((sum, e) => sum + monthlyNetto(e), 0)

  return (
    <Card>
      <Card.Header>
        <div className="flex justify-between items-center">
          <div>
            <span className="text-h5">Personalplanung</span>
            <p className="text-small text-gray-500">
              Speist die GuV-Position „Personalkosten". Nebenkosten-Default: Vollzeit/Teilzeit 22 %, Minijob 30 % (pro Mitarbeiter überschreibbar).
            </p>
          </div>
          <Button variant="primary" onClick={() => { setEditEntry(null); setModalOpen(true) }}>
            <Plus className="size-4 mr-1" /> Mitarbeiter
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <SimpleTable>
          <SimpleTable.Head>
            <SimpleTable.Tr>
              <SimpleTable.Th>Name</SimpleTable.Th>
              <SimpleTable.Th>Art</SimpleTable.Th>
              <SimpleTable.Th>Zeitraum</SimpleTable.Th>
              <SimpleTable.Th className="text-right">Brutto/Monat</SimpleTable.Th>
              <SimpleTable.Th className="text-right">Nebenkosten</SimpleTable.Th>
              <SimpleTable.Th>Förderung</SimpleTable.Th>
              <SimpleTable.Th className="text-right">Aufwand/Monat</SimpleTable.Th>
              <SimpleTable.Th></SimpleTable.Th>
            </SimpleTable.Tr>
          </SimpleTable.Head>
          <SimpleTable.Body>
            {!loading && entries.length === 0 && (
              <SimpleTable.Tr>
                <SimpleTable.Td colSpan={8} className="text-center text-small text-gray-500 py-6">
                  Noch keine Mitarbeiter — z.B. „Mitarbeiter A ab 10/2026, 3000 € brutto, Vollzeit" anlegen.
                </SimpleTable.Td>
              </SimpleTable.Tr>
            )}
            {entries.map((e) => (
              <SimpleTable.Tr key={e.id}>
                <SimpleTable.Td>{e.name}</SimpleTable.Td>
                <SimpleTable.Td className="text-small text-gray-600">{TYPE_LABEL[e.employmentType]}</SimpleTable.Td>
                <SimpleTable.Td className="text-small">{fmtPeriod(e)}</SimpleTable.Td>
                <SimpleTable.Td className="text-right">{EUR.format(e.grossMonthly)}</SimpleTable.Td>
                <SimpleTable.Td className="text-right text-small text-gray-600">
                  {e.nebenkostenRatePct ?? DEFAULT_RATE[e.employmentType]} %
                </SimpleTable.Td>
                <SimpleTable.Td className="text-small text-gray-600">
                  {e.foerderungMode === 'none' || e.foerderungValue == null
                    ? '—'
                    : e.foerderungMode === 'percent'
                      ? `${e.foerderungValue} %`
                      : EUR.format(e.foerderungValue)}
                </SimpleTable.Td>
                <SimpleTable.Td className="text-right font-medium">{EUR.format(monthlyNetto(e))}</SimpleTable.Td>
                <SimpleTable.Td className="text-right whitespace-nowrap">
                  <IconButton
                    icon={<Pencil className="size-4" />}
                    aria-label="Bearbeiten"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setEditEntry(e); setModalOpen(true) }}
                  />
                  <IconButton
                    icon={<Trash2 className="size-4" />}
                    aria-label="Löschen"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(e.id)}
                  />
                </SimpleTable.Td>
              </SimpleTable.Tr>
            ))}
            {entries.length > 0 && (
              <SimpleTable.Tr>
                <SimpleTable.Td colSpan={6} className="text-right font-medium">Summe Personalaufwand/Monat</SimpleTable.Td>
                <SimpleTable.Td className="text-right font-semibold">{EUR.format(totalNetto)}</SimpleTable.Td>
                <SimpleTable.Td></SimpleTable.Td>
              </SimpleTable.Tr>
            )}
          </SimpleTable.Body>
        </SimpleTable>
      </Card.Body>

      <PersonnelModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        planId={planId}
        defaultYear={year}
        entry={editEntry}
        onSaved={() => { setModalOpen(false); void load() }}
      />
    </Card>
  )
}

function PersonnelModal({
  open, onOpenChange, planId, defaultYear, entry, onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  planId: number
  defaultYear: number
  entry: PersonnelEntry | null
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [employmentType, setEmploymentType] = useState<EmploymentType>('vollzeit')
  const [grossMonthly, setGrossMonthly] = useState(0)
  const [startYear, setStartYear] = useState(defaultYear)
  const [startMonth, setStartMonth] = useState(1)
  const [endYear, setEndYear] = useState<number | null>(null)
  const [endMonth, setEndMonth] = useState<number | null>(null)
  const [nebenkostenRatePct, setNebenkostenRatePct] = useState<number | null>(null)
  const [foerderungMode, setFoerderungMode] = useState<FoerderungMode>('none')
  const [foerderungValue, setFoerderungValue] = useState<number | null>(null)
  const [foerderungStartMonth, setFoerderungStartMonth] = useState<number | null>(null)
  const [foerderungEndMonth, setFoerderungEndMonth] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (entry) {
      setName(entry.name)
      setEmploymentType(entry.employmentType)
      setGrossMonthly(entry.grossMonthly)
      setStartYear(entry.startYear)
      setStartMonth(entry.startMonth)
      setEndYear(entry.endYear)
      setEndMonth(entry.endMonth)
      setNebenkostenRatePct(entry.nebenkostenRatePct)
      setFoerderungMode(entry.foerderungMode)
      setFoerderungValue(entry.foerderungValue)
      setFoerderungStartMonth(entry.foerderungStartMonth)
      setFoerderungEndMonth(entry.foerderungEndMonth)
    } else {
      setName(''); setEmploymentType('vollzeit'); setGrossMonthly(0)
      setStartYear(defaultYear); setStartMonth(1); setEndYear(null); setEndMonth(null)
      setNebenkostenRatePct(null); setFoerderungMode('none'); setFoerderungValue(null)
      setFoerderungStartMonth(null); setFoerderungEndMonth(null)
    }
    setError(null)
  }, [open, entry, defaultYear])

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        name,
        employmentType,
        grossMonthly,
        startYear,
        startMonth,
        endYear,
        endMonth,
        nebenkostenRatePct,
        foerderungMode,
        foerderungValue: foerderungMode === 'none' ? null : foerderungValue,
        foerderungStartYear: foerderungStartMonth != null ? defaultYear : null,
        foerderungStartMonth,
        foerderungEndYear: foerderungEndMonth != null ? defaultYear : null,
        foerderungEndMonth,
      }
      if (entry) {
        await apiClient.put(`/liquidity-planning/plans/${planId}/personnel/${entry.id}`, payload)
      } else {
        await apiClient.post(`/liquidity-planning/plans/${planId}/personnel`, payload)
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={entry ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting || !name || grossMonthly <= 0}>
            {submitting ? 'Speichere…' : entry ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Mitarbeiter A" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-small font-medium text-gray-700 mb-1 block">Beschäftigungsart</label>
            <SelectRoot value={employmentType} onValueChange={(v) => setEmploymentType(v as EmploymentType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vollzeit">Vollzeit</SelectItem>
                <SelectItem value="teilzeit">Teilzeit</SelectItem>
                <SelectItem value="minijob">Minijob</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>
          <Input
            label="Brutto/Monat (€)"
            type="number" step="100"
            value={grossMonthly}
            onChange={(e) => setGrossMonthly(Number(e.target.value))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Eintritt-Monat" type="number" min={1} max={12} value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} />
          <Input label="Eintritt-Jahr" type="number" value={startYear} onChange={(e) => setStartYear(Number(e.target.value))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Austritt-Monat (optional)"
            type="number" min={1} max={12}
            value={endMonth ?? ''}
            onChange={(e) => setEndMonth(e.target.value ? Number(e.target.value) : null)}
          />
          <Input
            label="Austritt-Jahr (optional)"
            type="number"
            value={endYear ?? ''}
            onChange={(e) => setEndYear(e.target.value ? Number(e.target.value) : null)}
          />
        </div>
        <Input
          label={`Lohnnebenkosten % (leer = Default ${DEFAULT_RATE[employmentType]} %)`}
          type="number" step="0.5"
          value={nebenkostenRatePct ?? ''}
          onChange={(e) => setNebenkostenRatePct(e.target.value ? Number(e.target.value) : null)}
        />
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Förderung</label>
          <SelectRoot value={foerderungMode} onValueChange={(v) => setFoerderungMode(v as FoerderungMode)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keine</SelectItem>
              <SelectItem value="percent">Prozentual (% vom Aufwand)</SelectItem>
              <SelectItem value="amount">Fester Betrag (€/Monat)</SelectItem>
            </SelectContent>
          </SelectRoot>
        </div>
        {foerderungMode !== 'none' && (
          <div className="grid grid-cols-3 gap-3">
            <Input
              label={foerderungMode === 'percent' ? 'Förderung %' : 'Förderung €/Monat'}
              type="number"
              value={foerderungValue ?? ''}
              onChange={(e) => setFoerderungValue(e.target.value ? Number(e.target.value) : null)}
            />
            <Input
              label="Förder-Start-Monat"
              type="number" min={1} max={12}
              value={foerderungStartMonth ?? ''}
              onChange={(e) => setFoerderungStartMonth(e.target.value ? Number(e.target.value) : null)}
              hint="leer = ab Eintritt"
            />
            <Input
              label="Förder-End-Monat"
              type="number" min={1} max={12}
              value={foerderungEndMonth ?? ''}
              onChange={(e) => setFoerderungEndMonth(e.target.value ? Number(e.target.value) : null)}
              hint="leer = bis Austritt"
            />
          </div>
        )}
        {error && <p className="text-small text-red-600">{error}</p>}
      </div>
    </Modal>
  )
}
